import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '@/hooks/useAuthState';
import { UserService } from '@/services/userService';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/layouts/AdminLayout';
import { getUserImageUrl } from '@/config/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Edit, Users, Mail, Phone, Calendar, Shield } from 'lucide-react';

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  profile_photo?: string;
  user_type: 'guest' | 'owner' | 'admin';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminViewUser = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, userType, userName, userPhoto, userId, loading: authLoading } = useAuthState();
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Verificar se é admin (AdminLayout já faz isso)

  // Carregar dados do usuário
  useEffect(() => {
    const loadUser = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Carregar dados reais do usuário do banco
        const userData = await UserService.getUserById(parseInt(id));
        
        if (!userData) {
          toast({
            title: "Erro",
            description: "Usuário não encontrado",
            variant: "destructive",
          });
          navigate('/admin/usuarios');
          return;
        }
        
        // Verificar se é um administrador
        if (userData.user_type === 'admin') {
          toast({
            title: "Acesso Negado",
            description: "Não é permitido visualizar dados de administradores por questões de segurança",
            variant: "destructive",
          });
          navigate('/admin/usuarios');
          return;
        }
        
        setUser(userData);
      } catch (error) {
        console.error('Erro ao carregar usuário:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os dados do usuário",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id, toast, navigate]);

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'guest': return 'Hóspede';
      case 'owner': return 'Proprietário';
      case 'admin': return 'Administrador';
      default: return type;
    }
  };

  const getUserTypeColor = (type: string) => {
    switch (type) {
      case 'guest': return 'bg-blue-100 text-blue-800';
      case 'owner': return 'bg-green-100 text-green-800';
      case 'admin': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEdit = () => {
    navigate(`/admin/usuarios/editar/${id}`);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Usuário não encontrado</h1>
            <Button onClick={() => navigate('/admin/usuarios')}>
              Voltar para Lista
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin/usuarios')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Detalhes do Usuário</h1>
              <p className="text-gray-600">Visualize as informações completas do usuário</p>
    </div>
    </div>
          
          <Button onClick={handleEdit} className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            Editar Usuário
          </Button>
    </div>

        {/* Informações do Usuário */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações Principais */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Informações Pessoais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Foto do Usuário */}
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
                    {user.profile_photo ? (
                      <img 
                        src={getUserImageUrl(user.id, user.profile_photo)} 
                        alt={user.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <Users className={`h-8 w-8 text-primary ${user.profile_photo ? 'hidden' : ''}`} />
    </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{user.name}</h2>
                    <p className="text-gray-600">{user.email}</p>
    </div>
    </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nome Completo</label>
                    <p className="text-lg font-semibold text-gray-900">{user.name}</p>
    </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-lg text-gray-900 flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {user.email}
                    </p>
    </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Telefone</label>
                    <p className="text-lg text-gray-900 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {user.phone || 'Não informado'}
                    </p>
    </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">Tipo de Usuário</label>
                    <div className="mt-1">
                      <Badge className={getUserTypeColor(user.user_type)}>
                        {getUserTypeLabel(user.user_type)}
                      </Badge>
    </div>
    </div>
    </div>
              </CardContent>
            </Card>
    </div>

          {/* Status e Informações do Sistema */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Status do Sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Status da Conta</label>
                  <div className="mt-2">
                    <Badge variant={user.is_active ? 'default' : 'secondary'}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
    </div>
    </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">ID do Usuário</label>
                  <p className="text-lg font-mono text-gray-900">#{user.id}</p>
    </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Datas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Data de Cadastro</label>
                  <p className="text-lg text-gray-900">
                    {new Date(user.created_at).toLocaleDateString('pt-BR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
    </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Última Atualização</label>
                  <p className="text-lg text-gray-900">
                    {new Date(user.updated_at).toLocaleDateString('pt-BR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
    </div>
              </CardContent>
            </Card>
    </div>
    </div>
    </div>
    </AdminLayout>
  );
};

export default AdminViewUser;
